import { Plus, Trash2 } from "lucide-react";
import {
  SHIPPING_CONDITION_KINDS,
  SHIPPING_CONDITION_LABELS,
  createEmptyShippingRule,
  parseShippingAdvancedConfig,
  type ShippingAdvancedConfig,
  type ShippingCondition,
  type ShippingConditionKind,
} from "@gaole/shared";
import { Button, Input, NumberField, SearchSelect, SettingsCard, Toggle } from "../../components/ui";

function conditionSummary(condition: ShippingCondition): string {
  switch (condition.kind) {
    case "total_weight_kg_gte":
      return `Weight ≥ ${condition.kg} kg`;
    case "total_weight_kg_lte":
      return `Weight ≤ ${condition.kg} kg`;
    case "product_type_qty_gte":
      return `${condition.productType} qty ≥ ${condition.qty}`;
    case "distinct_product_types_gte":
      return `Distinct types ≥ ${condition.count}`;
    case "physical_units_gte":
      return `Units ≥ ${condition.qty}`;
    default:
      return "Condition";
  }
}

function ConditionEditor({
  condition,
  productTypeOptions,
  onChange,
  onRemove,
}: {
  condition: ShippingCondition;
  productTypeOptions: { value: string; label: string }[];
  onChange: (next: ShippingCondition) => void;
  onRemove: () => void;
}) {
  return (
    <div className="rounded-xl border border-line bg-surface-2 p-3">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1 space-y-2">
          <SearchSelect
            label="Condition"
            options={SHIPPING_CONDITION_KINDS.map((kind) => ({
              value: kind,
              label: SHIPPING_CONDITION_LABELS[kind],
            }))}
            value={condition.kind}
            onChange={(kind) => {
              const next = kind as ShippingConditionKind;
              if (next === "total_weight_kg_gte") onChange({ kind: next, kg: 1 });
              else if (next === "total_weight_kg_lte") onChange({ kind: next, kg: 1 });
              else if (next === "product_type_qty_gte") {
                onChange({
                  kind: next,
                  productType: productTypeOptions[0]?.value ?? "SINGLE",
                  qty: 1,
                });
              } else if (next === "distinct_product_types_gte") onChange({ kind: next, count: 2 });
              else onChange({ kind: "physical_units_gte", qty: 1 });
            }}
          />
          {(condition.kind === "total_weight_kg_gte" || condition.kind === "total_weight_kg_lte") && (
            <NumberField
              label="Kilograms"
              decimal
              step="0.01"
              min={0}
              value={condition.kg}
              onChange={(kg) => onChange({ ...condition, kg })}
            />
          )}
          {condition.kind === "product_type_qty_gte" && (
            <>
              <SearchSelect
                label="Product type"
                options={productTypeOptions}
                value={condition.productType}
                onChange={(productType) => onChange({ ...condition, productType })}
              />
              <NumberField
                label="Minimum quantity"
                min={1}
                value={condition.qty}
                onChange={(qty) => onChange({ ...condition, qty: Math.max(1, qty) })}
              />
            </>
          )}
          {condition.kind === "distinct_product_types_gte" && (
            <NumberField
              label="Minimum distinct types"
              min={1}
              value={condition.count}
              onChange={(count) => onChange({ ...condition, count: Math.max(1, count) })}
            />
          )}
          {condition.kind === "physical_units_gte" && (
            <NumberField
              label="Minimum units"
              min={1}
              value={condition.qty}
              onChange={(qty) => onChange({ ...condition, qty: Math.max(1, qty) })}
            />
          )}
        </div>
        <button
          type="button"
          className="mt-6 text-muted-2 hover:text-red-500"
          aria-label="Remove condition"
          onClick={onRemove}
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

export function ShippingAdvancedEditor({
  value,
  productTypeOptions,
  onChange,
  footer,
}: {
  value: unknown;
  productTypeOptions: { value: string; label: string }[];
  onChange: (next: ShippingAdvancedConfig) => void;
  footer?: React.ReactNode;
}) {
  const config = parseShippingAdvancedConfig(value);

  const update = (patch: Partial<ShippingAdvancedConfig>) => {
    onChange({ ...config, ...patch });
  };

  return (
    <SettingsCard
      title="Advanced surcharges"
      description="When enabled, matching rules stack on top of the country/state base fee."
      footer={footer}
    >
      <Toggle
        label="Enable advanced surcharges"
        checked={config.enabled}
        onChange={(enabled) => update({ enabled })}
      />

      <div className="mt-4 space-y-3">
        {config.rules.map((rule, ruleIndex) => (
          <div key={rule.id} className="rounded-xl border border-line bg-surface-2 p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="grid flex-1 gap-3 sm:grid-cols-2">
                <Input
                  label="Rule label"
                  value={rule.label}
                  onChange={(e) => {
                    const rules = [...config.rules];
                    rules[ruleIndex] = { ...rule, label: e.target.value };
                    update({ rules });
                  }}
                />
                <NumberField
                  label="Surcharge (RM)"
                  decimal
                  step="0.01"
                  min={0}
                  value={rule.fee}
                  onChange={(fee) => {
                    const rules = [...config.rules];
                    rules[ruleIndex] = { ...rule, fee };
                    update({ rules });
                  }}
                />
              </div>
              <button
                type="button"
                className="mt-6 text-muted-2 hover:text-red-500"
                aria-label="Remove rule"
                onClick={() => update({ rules: config.rules.filter((_, i) => i !== ruleIndex) })}
              >
                <Trash2 size={14} />
              </button>
            </div>

            <div className="mt-3 space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-2">
                Conditions (all must match)
              </p>
              {rule.conditions.map((condition, conditionIndex) => (
                <ConditionEditor
                  key={`${rule.id}-${conditionIndex}`}
                  condition={condition}
                  productTypeOptions={productTypeOptions}
                  onChange={(next) => {
                    const rules = [...config.rules];
                    const conditions = [...rule.conditions];
                    conditions[conditionIndex] = next;
                    rules[ruleIndex] = { ...rule, conditions };
                    update({ rules });
                  }}
                  onRemove={() => {
                    const rules = [...config.rules];
                    rules[ruleIndex] = {
                      ...rule,
                      conditions: rule.conditions.filter((_, i) => i !== conditionIndex),
                    };
                    update({ rules });
                  }}
                />
              ))}
              <Button
                size="sm"
                variant="outline"
                className="inline-flex items-center gap-1.5"
                onClick={() => {
                  const rules = [...config.rules];
                  rules[ruleIndex] = {
                    ...rule,
                    conditions: [...rule.conditions, { kind: "physical_units_gte", qty: 1 }],
                  };
                  update({ rules });
                }}
              >
                <Plus size={14} aria-hidden />
                Add condition
              </Button>
              {!rule.conditions.length && (
                <p className="text-[11px] text-amber-300/90">Add at least one condition for this rule to apply.</p>
              )}
              {rule.conditions.length > 0 && (
                <p className="text-[10px] text-muted-2">{rule.conditions.map(conditionSummary).join(" · ")}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      <Button
        size="sm"
        variant="outline"
        className="mt-4 inline-flex items-center gap-1.5"
        onClick={() => update({ rules: [...config.rules, createEmptyShippingRule()] })}
      >
        <Plus size={14} aria-hidden />
        Add surcharge rule
      </Button>
    </SettingsCard>
  );
}
